import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGlobalReducer } from "../../../GlobalContext";
import { Create } from "../../api/recordService";
import { GetWarehouseSources } from "../../api/dataImportService";
import { isFailedResponse } from "../../api/request";
import { FileUploadField } from "../../blocks/fields/FileUploadField";
import { PicklistField } from "../../blocks/fields/PicklistField";
import { PicklistOption } from "../../blocks/fields/PicklistField.types";
import { Button } from "../../elements/Button";
import { ConfirmationModal } from "../../elements/ConfirmationModal";
import { useLookupOptions } from "../../hooks/useLookupOptions";
import { DataImportHelp } from "./DataImportHelp";

const MaxFileSize = 15 * 1024 * 1024;

interface FormErrors {
  source?: string;
  file?: string;
}

export function validateFile(file: File) {
  const isCsv =
    file.type === "text/csv" && file.name.toLowerCase().endsWith(".csv");

  if (!isCsv) {
    return "Only .csv files are supported";
  }

  return file.size > MaxFileSize ? "File can not be larger than 15MB" : "";
}

function readBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** The source, list and file for a new import, and the button that starts it. */
export function DataImportForm() {
  const globalDispatch = useGlobalReducer();
  const navigate = useNavigate();
  const { options: lists } = useLookupOptions("list");
  const [sources, setSources] = useState<PicklistOption[]>([]);
  const [source, setSource] = useState("");
  const [list, setList] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    GetWarehouseSources(globalDispatch).then((response) => {
      if (!cancelled && Array.isArray(response?.data)) {
        setSources(
          response.data.map((item: { ClientId: string; Label: string }) => ({
            Key: item.ClientId,
            Value: item.Label,
          }))
        );
      }
    });

    return () => {
      cancelled = true;
    };
  }, [globalDispatch]);

  const pickFile = (picked: File | null) => {
    const fileError = picked ? validateFile(picked) : "";
    setFile(picked && !fileError ? picked : null);
    setErrors((current) => ({ ...current, file: fileError || undefined }));
  };

  const requestImport = () => {
    const nextErrors: FormErrors = {
      source: source ? undefined : "Source is required",
      file: errors.file ?? (file ? undefined : "File is required"),
    };
    setErrors(nextErrors);

    if (!nextErrors.source && !nextErrors.file) {
      setIsConfirming(true);
    }
  };

  const startImport = async () => {
    if (!file) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await Create(
        globalDispatch,
        "",
        {
          Id: undefined,
          Name: undefined,
          Attributes: {
            source,
            list: list || undefined,
            file: {
              Name: file.name,
              Base64String: await readBase64(file),
              MimeType: file.type,
              FileSize: file.size,
            },
          },
        },
        "dataimport"
      );

      if (!isFailedResponse(response) && response.data) {
        navigate(`/data-import?id=${response.data}`, { replace: true });
        return;
      }
    } finally {
      setIsConfirming(false);
      setIsSubmitting(false);
    }
  };

  return (
    <form
      name="dataImport"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        requestImport();
      }}
    >
      <div className="d-flex justify-content-end">
        <DataImportHelp />
      </div>
      <div className="row">
        <PicklistField
          label="Source"
          name="source"
          value={source}
          options={sources}
          required
          helpText="Select the data source to import users from."
          error={errors.source}
          disabled={isSubmitting}
          className="col-md-4"
          onChange={(value) => {
            setSource(value ?? "");
            setErrors((current) => ({ ...current, source: undefined }));
          }}
        />
        <PicklistField
          label="Static Marketing List"
          name="list"
          value={list}
          options={lists}
          helpText="Select an existing Static Marketing List to add the contacts to. If no list is selected, a new Static Marketing List will be created automatically."
          disabled={isSubmitting}
          className="col-md-4"
          onChange={(value) => setList(value ?? "")}
        />
        <FileUploadField
          label="File"
          name="file"
          accept=".csv,text/csv"
          currentFileName={file?.name}
          helpText="Upload a .csv file (max 15MB)."
          error={errors.file}
          disabled={isSubmitting}
          className="col-md-4"
          onChange={pickFile}
        />
      </div>
      <div className="d-flex justify-content-end">
        <Button
          text="Import"
          variant="primary"
          type="submit"
          disabled={isSubmitting}
          showSpinner={isSubmitting}
        />
      </div>
      {isConfirming && (
        <ConfirmationModal
          modalTitle="Initiate Import"
          confirmText="Import"
          isSubmitting={isSubmitting}
          onClose={() => setIsConfirming(false)}
          onConfirm={startImport}
        >
          Are you sure you want to begin the import?
        </ConfirmationModal>
      )}
    </form>
  );
}

export default DataImportForm;
